import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from './store/auth';
import Login from './pages/Login';
import Embed from './pages/Embed';
import { AppShell } from './components/shell/AppShell';
import Workspace from './pages/agent/Workspace';
import ActiveCalls from './pages/agent/ActiveCalls';
import Settings from './pages/agent/Settings';
import Activity from './pages/agent/Activity';
import PerformancePage from './pages/agent/Performance';
import ComingSoon from './pages/ComingSoon';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSpeedDials from './pages/admin/AdminSpeedDials';
import AdminCrmTemplates from './pages/admin/AdminCrmTemplates';
import { AdminGate } from './components/AdminGate';
import LeaderAgents from './pages/leader/Agents';
import LeaderDashboard from './pages/leader/Dashboard';
import QueueStatus from './pages/leader/QueueStatus';

/**
 * ArkConnect uygulama rotaları.
 *
 *  /login, /embed         → kendi başlarına render
 *  / (ve diğerleri)       → AppShell altında nested routes
 *  /admin/*               → AppShell + AdminGate (2FA password)
 *
 * AppShell L-chrome (topbar + sidebar) + global modals'ı render eder, child
 * route Outlet ile main alana yerleşir.
 */
export default function App() {
  const { token, user, restore, restoring } = useAuth();

  useEffect(() => { restore(); }, [restore]);

  // Hydrate-then-render: restore tamamlanmadan routing kararı verme,
  // yoksa kullanıcı refresh sonrası bir an Login'e atılır (race condition).
  if (restoring) return <SessionSplash />;

  const isAdmin = token && user?.role === 'admin';
  const isLeader = token && (user?.role === 'leader' || user?.role === 'admin');

  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/embed" element={<Embed />} />

      {/* ArkConnect shell — auth gerekli */}
      <Route
        path="/"
        element={token ? <AppShell /> : <Navigate to="/login" replace />}
      >
        {/* Ana sayfa: lider/admin → Takım Paneli (Yönetim Paneli), agent → Çalışma Alanı.
            Eskiden lider de agent Workspace'ini görüyordu (kritik açık) — düzeltildi. */}
        <Route index element={isLeader ? <LeaderDashboard /> : <Workspace />} />
        <Route path="active-calls" element={<ActiveCalls />} />
        <Route path="activity" element={<Activity />} />
        <Route path="performance" element={<PerformancePage />} />
        <Route path="settings" element={<Settings />} />

        {/* Takım Lideri sayfaları — leader veya admin erişebilir */}
        <Route path="agents" element={isLeader ? <LeaderAgents /> : <Navigate to="/" replace />} />
        <Route path="queue-status" element={isLeader ? <QueueStatus /> : <Navigate to="/" replace />} />
        {/* Eski /live-calls linkini koru (bookmark/geri tuşu) → Kuyruk Durumu'na yönlendir. */}
        <Route path="live-calls" element={<Navigate to="/queue-status" replace />} />
        <Route path="reports" element={isLeader ? <ComingSoon title="Raporlar" hint="Vardiya, gün, hafta bazlı çağrı ve mola raporları, Excel export." /> : <Navigate to="/" replace />} />

        {/* Admin sayfaları — AdminGate (X-Admin-Pass 2FA) içeride aktif */}
        <Route
          path="admin"
          element={isAdmin ? <AdminGate><Outlet /></AdminGate> : <Navigate to="/" replace />}
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="speed-dials" element={<AdminSpeedDials />} />
          <Route path="crm-templates" element={<AdminCrmTemplates />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Minimal splash — auth restore sırasında 100-500ms görünür. */
function SessionSplash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/40">
      <div className="text-center">
        <div className="text-[26px] font-bold tracking-tight leading-none mb-4">
          <span className="text-slate-900">Ark</span><span className="text-brand-500 font-semibold">Connect</span><span className="text-brand-500">.</span>
        </div>
        <div className="inline-block w-5 h-5 rounded-full border-2 border-brand-200 border-t-brand-500 animate-spin" />
      </div>
    </div>
  );
}
